import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

const extractYoutubeId = (url) => {
  if (!url) return null
  const patterns = [/[?&]v=([^&#]+)/, /youtu\.be\/([^?&#]+)/, /youtube\.com\/embed\/([^?&#]+)/]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

/**
 * ReactiveJSON primitive — embeds a YouTube video (16:9 aspect ratio).
 * Renders a placeholder play-icon block when the URL is empty or invalid.
 *
 * YAML usage:
 *   - type: YoutubeEmbed
 *     url: ~~.videoUrl
 *     title: ~~.title           # optional, used as iframe title
 *     attributes:
 *       class: "rounded-xl overflow-hidden shadow-lg"
 */
export const YoutubeEmbed = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const url = resolve(props.url)
  const title = resolve(props.title) ?? ''
  const videoId = extractYoutubeId(url)
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})

  return (
    <ActionDependant {...props}>
      <div {...evaluatedAttrs}>
        {videoId ? (
          <div className="relative" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        ) : (
          <div className="relative bg-gray-100" style={{ paddingBottom: '56.25%' }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
              <svg width="48" height="34" viewBox="0 0 48 34" fill="none">
                <rect width="48" height="34" rx="8" fill="#E85D26" opacity="0.15" />
                <path d="M19 10l14 7-14 7V10z" fill="#E85D26" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </ActionDependant>
  )
}
